import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  documentId,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Search } from "lucide-react";
import Header from "./Header";

const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="flex items-center justify-center px-6 py-4 bg-[#060B26] border-t border-[#fefefe20]">
    <button
      type="button"
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 1}
      className="px-4 py-2 text-xl font-medium text-white/60 disabled:opacity-50 disabled:cursor-not-allowed hover:text-white transition-colors"
    >
      &lt;
    </button>
    <span className="text-sm text-white/60">
      Page {currentPage} of {totalPages}
    </span>
    <button
      type="button"
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      className="px-4 py-2 text-xl font-medium text-white/60 disabled:opacity-50 disabled:cursor-not-allowed hover:text-white transition-colors"
    >
      &gt;
    </button>
  </div>
);

const RECORDS_PER_PAGE = 8;

function formatOneToOneValue(v) {
  if (v === true) return "true";
  if (v === false) return "false";
  return "—";
}

const DiscountedUsers = ({ setIsSidebarOpen, isSidebarOpen }) => {
  const [initializing, setInitializing] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [fullUsersForSearch, setFullUsersForSearch] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const hasLoadedServerOnce = useRef(false);

  const searchActive = searchTerm.trim().length > 0;

  const discountedUsersQuery = useCallback(
    (constraints) =>
      query(
        collection(db, "users"),
        where("isDiscountedUser", "==", true),
        ...constraints
      ),
    []
  );

  const loadDiscountedCount = useCallback(async () => {
    const snap = await getCountFromServer(
      discountedUsersQuery([])
    );
    return snap.data().count;
  }, [discountedUsersQuery]);

  const fetchDiscountedPage = useCallback(
    async (targetPage) => {
      let lastDocSnap = null;
      let pageRows = [];

      for (let p = 1; p <= targetPage; p++) {
        const q = discountedUsersQuery([
          orderBy(documentId()),
          ...(lastDocSnap ? [startAfter(lastDocSnap)] : []),
          limit(RECORDS_PER_PAGE),
        ]);
        const snap = await getDocs(q);
        if (snap.empty) {
          pageRows = [];
          break;
        }
        pageRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        lastDocSnap = snap.docs[snap.docs.length - 1];
        if (p === targetPage) break;
      }

      return pageRows;
    },
    [discountedUsersQuery]
  );

  const loadAllDiscounted = useCallback(async () => {
    const q = discountedUsersQuery([orderBy(documentId())]);
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }, [discountedUsersQuery]);

  useEffect(() => {
    if (searchActive) return;
    let cancelled = false;
    (async () => {
      try {
        if (!hasLoadedServerOnce.current) {
          setInitializing(true);
        } else {
          setTableLoading(true);
        }
        const count = await loadDiscountedCount();
        if (cancelled) return;

        const rows = await fetchDiscountedPage(currentPage);
        if (cancelled) return;

        setTotalCount(count);
        setUsers(rows);
        hasLoadedServerOnce.current = true;
      } catch (e) {
        console.error("DiscountedUsers server page error:", e);
      } finally {
        if (!cancelled) {
          setInitializing(false);
          setTableLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentPage, searchActive, fetchDiscountedPage, loadDiscountedCount]);

  useEffect(() => {
    if (!searchActive) {
      setFullUsersForSearch(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setTableLoading(true);
        const allRows = await loadAllDiscounted();
        if (cancelled) return;
        setFullUsersForSearch(allRows);
        setCurrentPage(1);
      } catch (e) {
        console.error("DiscountedUsers search load error:", e);
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [searchTerm, searchActive, loadAllDiscounted]);

  const filteredUsers = useMemo(() => {
    const source = searchActive ? fullUsersForSearch || [] : users;
    const searchLower = searchTerm.toLowerCase().trim();
    if (!searchLower) return source;
    return source.filter((user) => {
      return (
        (user.pseudo || user.displayName || "")
          .toLowerCase()
          .includes(searchLower) ||
        (user.email || "").toLowerCase().includes(searchLower)
      );
    });
  }, [users, fullUsersForSearch, searchTerm, searchActive]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      (searchActive ? filteredUsers.length : totalCount) / RECORDS_PER_PAGE
    )
  );

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const paginatedUsers = useMemo(() => {
    if (searchActive) {
      const start = (currentPage - 1) * RECORDS_PER_PAGE;
      return filteredUsers.slice(start, start + RECORDS_PER_PAGE);
    }
    return users;
  }, [searchActive, filteredUsers, currentPage, users]);

  const handlePageChange = (newPage) => {
    setCurrentPage(Math.min(Math.max(1, newPage), totalPages));
  };

  const patchUserInLists = useCallback((userId, updates) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, ...updates } : u))
    );
    setFullUsersForSearch((prev) =>
      prev
        ? prev.map((u) => (u.id === userId ? { ...u, ...updates } : u))
        : prev
    );
  }, []);

  const handleToggleOneToOne = async (user) => {
    const next = user.isOneToOne !== true;
    setTogglingId(user.id);
    try {
      await updateDoc(doc(db, "users", user.id), { isOneToOne: next });
      patchUserInLists(user.id, { isOneToOne: next });
    } catch (e) {
      console.error("toggle isOneToOne:", e);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="rounded-sm font-poppins mx-0">
      <div className="w-[100%]">
        <Header
          PageName={"Discounted users"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>
      <div
        className="p-6 min-h-[calc(100vh-6rem)]"
        style={{
          background:
            "radial-gradient(ellipse 100% 70% at 50% -15%, rgba(56, 48, 120, 0.45) 0%, transparent 55%), linear-gradient(180deg, #0a0c2c 0%, #05071a 40%, #060B26 100%)",
        }}
      >
        {initializing ? (
          <div className="rounded-sm h-[70vh] flex justify-center items-center bg-[#060B26]/40 font-poppins">
            <div className="animate-spin w-16 h-16 border-4 border-primaryBlue border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="p-6 bg-[#060B26] opacity-75 rounded-sm">
            <div className="w-full bg-[#060B26] rounded-sm overflow-hidden relative">
              {tableLoading && (
                <div className="absolute inset-0 z-10 bg-[#060B26]/70 flex items-center justify-center">
                  <div className="animate-spin w-10 h-10 border-4 border-primaryBlue border-t-transparent rounded-full" />
                </div>
              )}
              <div className="flex flex-wrap justify-between items-center gap-3 border-b border-[#fefefe20] px-4 py-3">
                <h2 className="text-lg font-semibold text-white">
                  Discounted users
                </h2>
                <div className="relative shrink-0">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-10 pr-4 py-2 bg-white/5 border border-borderColor rounded-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primaryBlue/50"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 w-4 h-4" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#fefefe40]">
                  <thead className="bg-[#1c253600]">
                    <tr>
                      <th className="px-6 py-3 text-left text-[16px] font-normal text-gray-300 uppercase tracking-wider">
                        Display name
                      </th>
                      <th className="px-6 py-3 text-left text-[16px] font-normal text-gray-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-[16px] font-normal text-gray-300 uppercase tracking-wider">
                        isOneToOne
                      </th>
                      <th className="px-6 py-3 text-left text-[16px] font-normal text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[#060B26] opacity-75 divide-y divide-[#fefefe18]">
                    {paginatedUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-6 py-8 text-center text-gray-400 text-sm"
                        >
                          {searchActive && !fullUsersForSearch
                            ? "Loading search…"
                            : "No discounted users found."}
                        </td>
                      </tr>
                    ) : (
                      paginatedUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-white/[0.04]">
                          <td className="px-6 py-4 text-left whitespace-nowrap text-sm text-white">
                            {user.pseudo || user.displayName || "—"}
                          </td>
                          <td className="px-6 py-4 text-left whitespace-nowrap text-sm text-white">
                            {user.email || "—"}
                          </td>
                          <td className="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-300 font-mono">
                            {formatOneToOneValue(user.isOneToOne)}
                          </td>
                          <td className="px-6 py-4 text-left whitespace-nowrap text-sm">
                            <button
                              type="button"
                              disabled={togglingId === user.id}
                              onClick={() => handleToggleOneToOne(user)}
                              className="px-3 py-1.5 rounded-sm text-xs font-medium border border-[#fefefe30] text-white/90 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {togglingId === user.id
                                ? "…"
                                : user.isOneToOne === true
                                  ? "Disable 1:1"
                                  : "Enable 1:1"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountedUsers;
